import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Article } from '../lib/types';
import { useApiResource } from './useApiResource';

export function useKnowledgeBase() {
  const resource = useApiResource<Article[]>('/kb', []);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All documents');
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Article | null>(null);
  const [deleting, setDeleting] = useState<Article | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const articles = resource.data;
  const filtered = useMemo(() => articles.filter(article => {
    if (category !== 'All documents' && article.category !== category) return false;
    return `${article.title} ${article.content}`.toLowerCase().includes(query.toLowerCase());
  }), [articles, category, query]);
  const chunks = useMemo(() => articles.reduce((sum, article) => sum + article.chunks, 0), [articles]);
  const edit = (article: Article | null = null) => {setEditing(article); setOpen(true);};

  const upload = async (file?: File) => {
    if (!file || uploading) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Documents must be 5 MB or smaller.'); return; }
    setUploading(true); resource.setError('');
    try {
      const data = new FormData(); data.append('file', file);
      await api('/kb/upload', 'POST', data);
      toast.success('Document added. Ready for your next reply.');
      await resource.refresh();
    } catch (error: any) { resource.setError(error.message); toast.error(error.message); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await api(`/kb/${deleting.id}`, 'DELETE');
      setDeleting(null);
      toast.success('Article deleted.');
      await resource.refresh();
    } catch (error: any) { toast.error(error.message); }
    finally { setDeleteBusy(false); }
  };
  const clearFilters = () => {setQuery(''); setCategory('All documents');};
  return {...resource, articles, filtered, chunks, query, setQuery, category, setCategory,
    uploading, upload, open, setOpen, editing, edit, deleting, setDeleting, deleteBusy, remove, clearFilters};
}